import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import BackgroundWord from "./BackgroundWords";
import { useContent } from "../context/ContentContext";

export const Services = () => {
  const { content } = useContent();
  const services = content?.services || [];
  const navigate = useNavigate();

  const handleCta = (svc) => {
    if (svc.link) {
      if (svc.link.startsWith("/")) navigate(svc.link);
      else window.open(svc.link, "_blank", "noopener,noreferrer");
      return;
    }
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="relative bg-[#070707] py-32 md:py-44 overflow-hidden grain"
    >
      <BackgroundWord word="Leistungen" position="right" speed={0.4} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
              <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
              02 — Leistungen & Preise
            </div>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl max-w-3xl">
              Disziplinen. <br />
              <span className="text-zinc-500">Ein Standard. </span>
              <span className="text-glow-red text-white">Premium.</span>
            </h2>
          </div>
          <p className="text-zinc-500 text-sm max-w-sm leading-relaxed">
            Preise sind Startpunkte. Endgültige Pakete werden individuell im Beratungsgespräch zugeschnitten.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.map((s, i) => (
            <motion.article
              key={s.code + i}
              data-testid={`service-card-${s.code}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.06, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
              className="group relative bg-[#0A0A0A] border border-white/[0.06] p-6 md:p-7 flex flex-col min-h-[420px] md:min-h-[460px] transition-all duration-500 hover:border-[#ff0033]/40 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(255,0,51,0.4)]"
            >
              <div className="absolute top-5 right-5 red-led opacity-50 group-hover:opacity-100 transition-opacity" />
              <div className="flex items-baseline gap-3">
                <span className="font-display font-black text-[#ff0033] text-xs tracking-[0.2em]">{s.code}</span>
                <span className="block w-12 h-px bg-white/15" />
              </div>
              <h3 className="font-display font-black uppercase text-2xl md:text-3xl mt-6 leading-none break-words">{s.title}</h3>
              <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                <span className="font-display font-bold text-xl md:text-2xl text-white">{s.price}</span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{s.sub}</span>
              </div>
              <p className="mt-6 text-sm text-zinc-400 leading-relaxed flex-1">{s.description}</p>
              <ul className="mt-6 space-y-2.5">
                {(s.bullets || []).map((b) => (
                  <li key={b} className="flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase text-zinc-500">
                    <span className="block w-1.5 h-1.5 bg-[#ff0033] shadow-[0_0_6px_#ff0033]" />
                    {b}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`service-cta-${s.code}`}
                onClick={() => handleCta(s)}
                className="mt-8 inline-flex items-center justify-between gap-3 text-[11px] tracking-[0.25em] uppercase font-bold text-white border-t border-white/10 pt-5 group/btn"
              >
                <span>{s.cta || (s.link ? "Mehr erfahren" : "Anfragen")}</span>
                <ArrowUpRight
                  size={16}
                  className="text-[#ff0033] group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform duration-300"
                />
              </button>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 text-center text-xs tracking-[0.3em] uppercase text-zinc-600">
          Individuelle Pakete · Beratungsgespräch kostenlos
        </div>
      </div>
    </section>
  );
};

export default Services;
